import { FC, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Card,
  Checkbox,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { DividerWithText } from '@farmshare/ui-components';

export interface ItemOption {
  id: string;
  title?: string;
  description?: string;
  imageUrl?: string;
  /**
   * Optional group label used to bucket items into sections.
   * For cuts this is the primal name; for trim/specs it can be omitted.
   */
  groupLabel?: string;
}

export interface ItemPickerBodyProps {
  items: ItemOption[];
  /** IDs of items already present in the question/option */
  selectedIds: string[];
  /**
   * Called immediately when the user clicks the Add button on an un-added item.
   * Callers should update their state so `selectedIds` re-renders correctly.
   */
  onAdd: (id: string) => void;
  /** Called immediately when the user clicks the Remove button on an added item. */
  onRemove: (id: string) => void;
  /** MUI colour token for the avatar background, e.g. 'secondary.main' */
  accentColor?: string;
  /**
   * Maximum number of items that can be selected. When reached, selecting a new
   *  item replaces the current selection. Omit for unlimited.
   */
  maxSelections?: number;
}

/**
 * Immediate add/remove item picker used in questionnaire question modals.
 *
 * - Search bar filters across title, description, and group label.
 * - Items are grouped by `groupLabel` (named groups sorted alpha, ungrouped last).
 * - Each row shows an Add button (when not selected) or a Remove button (when selected).
 * - Actions fire immediately — no form submission needed; the parent modal
 *   only needs a "Close" button.
 * @param root0
 * @param root0.items
 * @param root0.selectedIds
 * @param root0.onAdd
 * @param root0.onRemove
 * @param root0.accentColor
 * @param root0.maxSelections
 */
export const ItemPickerBody: FC<ItemPickerBodyProps> = ({
  items,
  selectedIds: initialSelectedIds,
  onAdd,
  onRemove,
  accentColor = 'primary.main',
  maxSelections,
}) => {
  // Local state so Add/Remove button toggles are reflected immediately inside
  // the modal without waiting for the parent's data to flow back in.
  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(
      (item) =>
        item.title?.toLowerCase().includes(q) ||
        item.description?.toLowerCase().includes(q) ||
        item.groupLabel?.toLowerCase().includes(q),
    );
  }, [items, search]);

  /**
   * Build an ordered group list.
   * Named groups are sorted alphabetically; items with no groupLabel go last
   * under an implicit ungrouped bucket (empty-string key).
   */
  const groups = useMemo(() => {
    const map = new Map<string, ItemOption[]>();
    filtered.forEach((item) => {
      const key = item.groupLabel ?? '';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    });
    return [...map.entries()].sort(([a], [b]) => {
      if (!a) return 1;
      if (!b) return -1;
      return a.localeCompare(b);
    });
  }, [filtered]);

  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        No items available.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      {/* Search filter */}
      <TextField
        placeholder="Search…"
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {filtered.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No items match &ldquo;{search}&rdquo;.
        </Typography>
      ) : (
        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
          {groups.map(([groupLabel, groupItems], gi) => (
            <Box key={groupLabel || '__ungrouped__'}>
              {/* Section header — only rendered for named groups */}
              {groupLabel && <DividerWithText text={groupLabel} align="left" />}

              <List dense disablePadding>
                {groupItems.map((item) => {
                  const isAdded = selectedIds.includes(item.id);
                  return (
                    <ListItemButton
                      key={item.id}
                      disableGutters
                      onClick={() => {
                        if (isAdded) {
                          setSelectedIds((prev) =>
                            prev.filter((id) => id !== item.id),
                          );
                          onRemove(item.id);
                        } else {
                          if (
                            maxSelections !== undefined &&
                            selectedIds.length >= maxSelections
                          ) {
                            // Replace: evict all current selections first
                            selectedIds.forEach((id) => onRemove(id));
                            setSelectedIds([item.id]);
                          } else {
                            setSelectedIds((prev) => [...prev, item.id]);
                          }
                          onAdd(item.id);
                        }
                      }}
                    >
                      <ListItem disableGutters>
                        <ListItemAvatar sx={{ minWidth: 40 }}>
                          <Checkbox checked={isAdded} />
                        </ListItemAvatar>
                        <ListItemText
                          primary={item.title || 'Unnamed'}
                          secondary={item.description}
                          slotProps={{
                            primary: {
                              variant: 'body2',

                              color: isAdded ? 'text.disabled' : 'text.primary',
                            },
                            secondary: {
                              variant: 'caption',
                            },
                          }}
                        />
                      </ListItem>
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          ))}
        </Box>
      )}
    </Stack>
  );
};
